import * as THREE from 'three'
import { Heightfield } from '../terrain/heightfield'
import { clamp01 } from '../core/math'
import { radialTexture } from './textures'
import { HAZE } from './atmosphere'
import { SPRAY_SUN, SPRAY_SKY } from './spray'

/**
 * Spindrift: snow smoking off the ridgelines.
 *
 * A still mountain looks rendered; a mountain with weather looks real. This is
 * the cheapest form of weather there is — a few hundred soft sprites streaming
 * off the crests in gusts — and it earns more than its cost because it moves
 * *independently of the player*. Everything else that moves in the scene is
 * caused by the rider; spindrift is the mountain doing something on its own,
 * which is most of what "alive" means.
 *
 * Emitters are found from the heightfield's exposure map: the crests the wind
 * actually works. Gusts are sine-composed rather than random so the drift
 * breathes — real spindrift comes in pulses, not as a constant hiss. The wind
 * blows toward +x, the same bearing the flagged spars lean, because one
 * mountain has one wind.
 *
 * Same sprite pipeline as the skier's spray (lit fake-spherical points), and
 * cosmetic in exactly the same way: render-only, reads the sim, never feeds it.
 */

const MAX_PARTICLES = 420
const MAX_EMITTERS = 14
/** Wind, m/s, toward +x — matching WIND_BEARING in obstacleMesh. */
const WIND = 7

interface Emitter {
  x: number
  y: number
  z: number
  phase: number
  strength: number
}

export class Spindrift {
  readonly points: THREE.Points
  private readonly emitters: Emitter[] = []
  private readonly pos: Float32Array
  private readonly vel: Float32Array
  private readonly life: Float32Array
  private readonly maxLife: Float32Array
  private readonly size: Float32Array
  private readonly alpha: Float32Array
  private readonly baseAlpha: Float32Array
  private readonly geom: THREE.BufferGeometry
  private cursor = 0
  private carry = 0
  private time = 0
  private seed = 0x9d2c5681

  constructor(hf: Heightfield) {
    // --- Find the crests ---------------------------------------------------
    // Coarse scan, scored by wind exposure times height, thinned so emitters
    // spread down the course instead of crowding the windiest hundred metres.
    const found: Emitter[] = []
    const step = 6
    for (let iz = 2; iz < hf.rows - 2; iz += step) {
      for (let ix = 2; ix < hf.cols - 2; ix += step) {
        const i = hf.index(ix, iz)
        const exp = hf.exposure[i]!
        if (exp < 0.35) continue
        found.push({
          x: hf.xAt(ix), y: hf.heightAtIndex(ix, iz), z: hf.zAt(iz),
          phase: 0, strength: exp,
        })
      }
    }
    found.sort((a, b) => b.strength - a.strength)
    for (const e of found) {
      if (this.emitters.length >= MAX_EMITTERS) break
      if (this.emitters.some((o) => Math.hypot(o.x - e.x, o.z - e.z) < 45)) continue
      e.phase = this.rand() * Math.PI * 2
      this.emitters.push(e)
    }

    // --- The sprite pool ---------------------------------------------------
    this.pos = new Float32Array(MAX_PARTICLES * 3)
    this.vel = new Float32Array(MAX_PARTICLES * 3)
    this.life = new Float32Array(MAX_PARTICLES)
    this.maxLife = new Float32Array(MAX_PARTICLES)
    this.size = new Float32Array(MAX_PARTICLES)
    this.alpha = new Float32Array(MAX_PARTICLES)
    this.baseAlpha = new Float32Array(MAX_PARTICLES)
    for (let i = 0; i < MAX_PARTICLES; i++) this.pos[i * 3 + 1] = -10000

    this.geom = new THREE.BufferGeometry()
    this.geom.setAttribute('position', new THREE.BufferAttribute(this.pos, 3))
    this.geom.setAttribute('size', new THREE.BufferAttribute(this.size, 1))
    this.geom.setAttribute('alpha', new THREE.BufferAttribute(this.alpha, 1))
    this.geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6)

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: radialTexture(48, 0.22) },
        uSunDir: HAZE.sunDir,
        uSunCol: SPRAY_SUN,
        uSkyCol: SPRAY_SKY,
      },
      vertexShader: `
        attribute float size;
        attribute float alpha;
        varying float vAlpha;
        void main() {
          vAlpha = alpha;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / max(-mv.z, 1.0));
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        uniform sampler2D map;
        uniform vec3 uSunDir;
        uniform vec3 uSunCol;
        uniform vec3 uSkyCol;
        varying float vAlpha;
        void main() {
          vec4 t = texture2D(map, gl_PointCoord);
          vec2 d = gl_PointCoord * 2.0 - 1.0;
          float r2 = dot(d, d);
          if (r2 > 1.0) discard;
          vec3 n = normalize(vec3(d, sqrt(max(0.0, 1.0 - r2))));
          float lambert = max(dot(n, uSunDir), 0.0);
          vec3 lit = uSkyCol + uSunCol * (0.35 + 0.65 * lambert);
          gl_FragColor = vec4(lit, t.a * vAlpha);
          if (gl_FragColor.a < 0.01) discard;
        }`,
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
    })

    this.points = new THREE.Points(this.geom, mat)
    this.points.frustumCulled = false
    this.points.renderOrder = 2
  }

  private rand(): number {
    let s = this.seed
    s ^= s << 13
    s ^= s >>> 17
    s ^= s << 5
    this.seed = s >>> 0
    return this.seed / 4294967296
  }

  update(dt: number): void {
    this.time += dt

    // --- Spawn, in gusts ---------------------------------------------------
    for (const e of this.emitters) {
      // Two incommensurate sines, sharpened: long calm, short violent pulses.
      const g = Math.pow(clamp01(
        0.55 * Math.sin(this.time * 0.33 + e.phase) +
        0.45 * Math.sin(this.time * 0.117 + e.phase * 1.7) + 0.38,
      ), 2.2)
      if (g < 0.02) continue
      this.carry += g * e.strength * 30 * dt
      while (this.carry >= 1) {
        this.carry -= 1
        const i = this.cursor
        this.cursor = (this.cursor + 1) % MAX_PARTICLES
        const o = i * 3
        this.pos[o] = e.x + (this.rand() - 0.5) * 14
        this.pos[o + 1] = e.y + 0.5 + this.rand() * 1.5
        this.pos[o + 2] = e.z + (this.rand() - 0.5) * 14
        this.vel[o] = WIND * (0.7 + this.rand() * 0.7)
        this.vel[o + 1] = 0.6 + this.rand() * 1.6
        this.vel[o + 2] = (this.rand() - 0.5) * 2.5
        this.life[i] = 0
        this.maxLife[i] = 1.4 + this.rand() * 1.6
        this.size[i] = 1.7 + this.rand() * 2.6
        this.baseAlpha[i] = (0.20 + this.rand() * 0.22) * (0.4 + 0.6 * g)
      }
    }

    // --- Advance -----------------------------------------------------------
    for (let i = 0; i < MAX_PARTICLES; i++) {
      if (this.pos[i * 3 + 1]! < -9000) continue
      this.life[i]! += dt
      const t = this.life[i]! / this.maxLife[i]!
      if (t >= 1) {
        this.pos[i * 3 + 1] = -10000
        this.alpha[i] = 0
        continue
      }
      const o = i * 3
      // Streaming: the plume accelerates downwind and sinks as it thins.
      this.vel[o]! += WIND * 0.35 * dt
      this.vel[o + 1]! -= 1.1 * dt
      this.pos[o]! += this.vel[o]! * dt
      this.pos[o + 1]! += this.vel[o + 1]! * dt
      this.pos[o + 2]! += this.vel[o + 2]! * dt
      // Grow while fading — a puff disperses, it does not pop.
      this.size[i]! += 1.6 * dt
      const fade = t < 0.2 ? t / 0.2 : 1 - (t - 0.2) / 0.8
      this.alpha[i] = this.baseAlpha[i]! * fade
    }

    this.geom.attributes.position!.needsUpdate = true
    this.geom.attributes.size!.needsUpdate = true
    this.geom.attributes.alpha!.needsUpdate = true
  }
}
